import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import CalendarNav from "./CalendarNav";
import { Filter } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const DAY_PX = 28;
const ROW_PX = 28;

function iso(d) {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

/**
 * Cell colour by absence count.
 * 0 → transparent
 * 1 → light amber
 * 2 → amber
 * 3-4 → orange
 * 5+ → deep red
 */
function heatColor(count, peak) {
  if (!count) return "bg-[#1E1E1E]";
  // Use absolute thresholds for a stable scale across departments.
  if (count >= 5) return "bg-red-600 text-white";
  if (count >= 3) return "bg-orange-400 text-white";
  if (count >= 2) return "bg-amber-300";
  return "bg-amber-100";
}

export default function DepartmentHeatmap({ reference, bankHolidays = [] }) {
  const today = new Date();
  const [cursor, setCursor] = useState(startOfMonth(today));
  const [filters, setFilters] = useState({ department: "", supervisor_id: "", job_role: "" });
  const [data, setData] = useState({ dates: [], departments: [] });
  const [loading, setLoading] = useState(false);

  const { startIso, endIso } = useMemo(() => ({
    startIso: iso(startOfMonth(cursor)),
    endIso: iso(endOfMonth(cursor)),
  }), [cursor]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { start: startIso, end: endIso };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/admin/heatmap", { params })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ dates: [], departments: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startIso, endIso, JSON.stringify(filters)]); // eslint-disable-line

  const bhSet = useMemo(() => new Set((bankHolidays || []).map((b) => b.date)), [bankHolidays]);
  const bhName = useMemo(() => {
    const m = new Map();
    for (const b of bankHolidays || []) m.set(b.date, b.name);
    return m;
  }, [bankHolidays]);

  const todayIso = iso(today);

  const clearFilters = () => setFilters({ department: "", supervisor_id: "", job_role: "" });

  return (
    <div className="space-y-4" data-testid="department-heatmap">
      <CalendarNav
        cursor={cursor}
        onCursor={setCursor}
        testIdPrefix="heat"
      />

      <div className="flex flex-wrap gap-2 items-center text-sm">
        <Filter className="w-4 h-4 text-muted-foreground"/>
        <FilterSelect testid="heat-filter-dept" placeholder="All departments" value={filters.department}
          onChange={(v) => setFilters({ ...filters, department: v })}
          options={(reference?.departments || []).map((d) => ({ value: d.name, label: d.name }))}/>
        <FilterSelect testid="heat-filter-supervisor" placeholder="All supervisors" value={filters.supervisor_id}
          onChange={(v) => setFilters({ ...filters, supervisor_id: v })}
          options={(reference?.supervisors || []).map((s) => ({ value: s.id, label: s.name }))}/>
        <FilterSelect testid="heat-filter-role" placeholder="All job roles" value={filters.job_role}
          onChange={(v) => setFilters({ ...filters, job_role: v })}
          options={(reference?.job_roles || []).map((r) => ({ value: r, label: r }))}/>
        <Button data-testid="heat-clear-filters" variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
        <div className="ml-auto text-xs text-muted-foreground">{loading ? "Loading…" : `${data.departments.length} departments`}</div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden bg-[#1E1E1E]">
        <div className="overflow-x-auto">
          <div style={{ minWidth: 200 + data.dates.length * DAY_PX }}>
            {/* Header */}
            <div className="flex sticky top-0 z-20 bg-[#1E1E1E] border-b border-border">
              <div className="w-[200px] shrink-0 px-3 py-2 text-[11px] uppercase tracking-[0.08em] font-semibold text-muted-foreground border-r border-border bg-[#1E1E1E]">
                Department
              </div>
              <div className="flex" style={{ width: data.dates.length * DAY_PX }}>
                {data.dates.map((d) => {
                  const dateObj = new Date(d + "T00:00:00");
                  const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                  const isBh = bhSet.has(d);
                  const isToday = d === todayIso;
                  return (
                    <div
                      key={d}
                      data-testid={`heat-header-${d}`}
                      title={isBh ? `Bank holiday — ${bhName.get(d)}` : dateObj.toLocaleDateString()}
                      className={[
                        "shrink-0 text-center py-1 border-r border-border/60",
                        isWeekend ? "bg-[#141414]" : "bg-[#1E1E1E]",
                        isBh ? "bg-purple-100" : "",
                        isToday ? "ring-1 ring-primary ring-inset" : "",
                      ].join(" ")}
                      style={{ width: DAY_PX }}
                    >
                      <div className="text-[10px] uppercase text-muted-foreground">{dateObj.toLocaleDateString(undefined, { weekday: "narrow" })}</div>
                      <div className={`text-xs tabular-nums ${isToday ? "font-bold text-primary" : ""}`}>{dateObj.getDate()}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Body */}
            {data.departments.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">No data for the selected window/filters.</div>
            )}
            {data.departments.map((row) => (
              <div key={row.department} data-testid={`heat-row-${row.department}`} className="flex border-b border-border/60" style={{ minHeight: ROW_PX }}>
                <div className="w-[200px] shrink-0 px-3 py-1.5 border-r border-border bg-[#1E1E1E] sticky left-0 text-sm font-medium truncate flex items-center justify-between">
                  <span className="truncate">{row.department}</span>
                  <span className="text-[10px] tabular-nums text-muted-foreground ml-2">peak {row.peak}</span>
                </div>
                <div className="flex">
                  {data.dates.map((d, i) => {
                    const count = row.daily_counts[i] || 0;
                    const dateObj = new Date(d + "T00:00:00");
                    const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                    const isBh = bhSet.has(d);
                    const color = heatColor(count, row.peak);
                    return (
                      <div
                        key={d}
                        data-testid={`heat-cell-${row.department}-${d}`}
                        title={`${row.department} · ${d} · ${count} off${isBh ? ` · Bank holiday: ${bhName.get(d)}` : ""}`}
                        className={[
                          "shrink-0 border-r border-border/40 flex items-center justify-center text-[11px] tabular-nums",
                          color,
                          isWeekend && !count ? "bg-[#141414]" : "",
                          isBh && !count ? "bg-purple-50" : "",
                        ].join(" ")}
                        style={{ width: DAY_PX, height: ROW_PX }}
                      >
                        {count > 0 ? count : ""}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-3 rounded-sm bg-[#1E1E1E] border border-border"/>0</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-3 rounded-sm bg-amber-100"/>1</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-3 rounded-sm bg-amber-300"/>2</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-3 rounded-sm bg-orange-400"/>3–4</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-3 rounded-sm bg-red-600"/>5+</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-3 rounded-sm bg-purple-100 border border-purple-300"/>bank holiday</span>
      </div>
    </div>
  );
}

function FilterSelect({ testid, placeholder, value, onChange, options }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="h-9 max-w-[180px]"><SelectValue placeholder={placeholder}/></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all">{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
